<template>
  <div class="tooth-item"
       :class="[`arch-${arch}`, `type-${type}`, `condition-${tooth.condition || 'none'}`,
                { 'is-selected': isSelected }]">
    <button type="button"
            class="tooth-crown"
            :title="crownTitle"
            @click="handleToothClick">
      <img :src="toothSvg" alt="" class="tooth-svg" />
    </button>
    <svg class="tooth-faces" viewBox="0 0 20 20" @click.stop>
      <polygon v-for="face in faces"
               :key="face.surface"
               class="face"
               :class="{ 'is-active': face.active }"
               :points="face.points"
               @click="handleSurfaceClick(face.surface)">
        <title>{{ face.title }}</title>
      </polygon>
    </svg>
    <button type="button" class="tooth-number" @click="handleToothClick">
      {{ tooth.number }}
    </button>
  </div>
</template>

<script>
import toothHealthy from '@/assets/images/tooth/tooth-healthy.svg';
import toothCarious from '@/assets/images/tooth/tooth-carious.svg';
import toothRestored from '@/assets/images/tooth/tooth-restored.svg';
import toothCrown from '@/assets/images/tooth/tooth-crown.svg';
import toothRootCanal from '@/assets/images/tooth/tooth-root-canal.svg';
import toothMissing from '@/assets/images/tooth/tooth-missing.svg';
import {
  conditionLabel,
  getSurfaceDescription,
  getSurfaceLayout,
  getToothArch,
  getToothType,
} from '../helpers/odontogramaHelpers';

const ZONE_POINTS = {
  top: '0.5,0.5 19.5,0.5 13.5,6.5 6.5,6.5',
  right: '19.5,0.5 19.5,19.5 13.5,13.5 13.5,6.5',
  bottom: '19.5,19.5 0.5,19.5 6.5,13.5 13.5,13.5',
  left: '0.5,19.5 0.5,0.5 6.5,6.5 6.5,13.5',
  center: '6.5,6.5 13.5,6.5 13.5,13.5 6.5,13.5',
};

const CONDITION_IMAGES = {
  healthy: toothHealthy,
  carious: toothCarious,
  restored: toothRestored,
  crown: toothCrown,
  canal: toothRootCanal,
  missing: toothMissing,
};

export default {
  name: 'ToothItem',
  props: {
    tooth: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    arch() {
      return getToothArch(this.tooth.number);
    },
    type() {
      return getToothType(this.tooth.number);
    },
    toothSvg() {
      return CONDITION_IMAGES[this.tooth.condition] || CONDITION_IMAGES.healthy;
    },
    crownTitle() {
      const { number, condition } = this.tooth;
      return condition ? `Dente ${number} - ${conditionLabel(condition)}` : `Dente ${number}`;
    },
    faces() {
      return getSurfaceLayout(this.tooth.number).map(({ zone, surface }) => ({
        surface,
        points: ZONE_POINTS[zone],
        title: getSurfaceDescription(this.tooth.number, surface),
        active: this.isSurfaceActive(surface),
      }));
    },
  },
  methods: {
    handleToothClick(event) {
      this.$emit('tooth-clicked', this.tooth, event);
    },
    handleSurfaceClick(surface) {
      this.$emit('surface-clicked', this.tooth, surface);
    },
    isSurfaceActive(surface) {
      return !!this.tooth.surfaces && this.tooth.surfaces.includes(surface);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/_odontogram";

.tooth-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 4px 2px;
  border-radius: $border-radius;
  border: $border-width solid transparent;
  transition: background-color .2s ease, border-color .2s ease;

  &.arch-lower {
    flex-direction: column-reverse;
  }

  &:hover {
    background: $bg-color;
  }

  &.is-selected {
    border-color: $primary-color;
    background: lighten($primary-color, 45%);

    .tooth-number {
      color: $primary-color;
    }
  }

  .tooth-crown {
    border: 0;
    padding: 0;
    background: none;
    cursor: pointer;
    line-height: 0;

    .tooth-svg {
      width: 34px;
      height: 34px;
      display: block;
    }
  }

  &.type-deciduous .tooth-svg {
    width: 28px;
    height: 28px;
  }

  &.arch-upper .tooth-svg {
    transform: rotate(180deg);
  }

  .tooth-number {
    border: 0;
    padding: 0;
    background: none;
    cursor: pointer;
    font-size: $font-size-sm;
    font-weight: 600;
    color: $gray-color-dark;
    font-feature-settings: "tnum";
  }

  .tooth-faces {
    width: 26px;
    height: 26px;
    overflow: visible;

    .face {
      fill: $bg-color-light;
      stroke: $gray-color;
      stroke-width: .75;
      cursor: pointer;
      transition: fill .15s ease;

      &:hover {
        fill: lighten($primary-color, 40%);
      }

      &.is-active {
        fill: $primary-color;
        stroke: $bg-color-light;
      }
    }
  }

  @each $condition, $color in $tooth-condition-colors {
    &.condition-#{$condition} .tooth-faces .face.is-active {
      fill: $color;
    }
  }

  &.condition-missing {
    .tooth-faces {
      opacity: .35;
      pointer-events: none;
    }
  }
}
</style>
